import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState, type ChangeEvent, type FormEvent } from 'react';
import { api, ApiError } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText } from '../components/ui.js';

interface ProductDiff {
  steamId: number;
  name: string;
  reportUsd: string | null;
  derivedUsd: string | null;
  diffUsd: string;
}
interface Month {
  year: number;
  month: number;
  status: 'matched' | 'mismatch' | 'report_missing' | 'overridden';
  reportId: string | null;
  reportRevenueShareUsd: string | null;
  derivedRevenueShareUsd: string;
  diffs: ProductDiff[];
  roundingDiffs: ProductDiff[];
  reportOnly: Array<{ steamId: number; name: string; revenueShareUsd: string }>;
  override: { reason: string; createdAt: string; current: boolean } | null;
}
type UploadResult = { filename: string; ok: true; year: number; month: number; replaced: boolean; lines: number } | { filename: string; ok: false; message: string };

const usd = (v: string | null) => (v === null ? '-' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const period = (m: { year: number; month: number }) => `${m.year}-${String(m.month).padStart(2, '0')}`;
const STATUS: Record<Month['status'], { label: string; className: string }> = {
  matched: { label: 'Matched', className: 'text-emerald-700 dark:text-emerald-400' },
  mismatch: { label: 'Mismatch', className: 'font-semibold text-red-700 dark:text-red-400' },
  report_missing: { label: 'Report missing', className: 'font-semibold text-amber-700 dark:text-amber-400' },
  overridden: { label: 'Overridden', className: 'text-sky-700 dark:text-sky-400' },
};

function DiffTable({ title, rows }: { title: string; rows: ProductDiff[] }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <h3 className="font-medium">{title}</h3>
      <table className="mt-1 text-sm">
        <thead className="text-slate-500"><tr><th className="pr-6 text-left">Product</th><th className="pr-6 text-right">Report</th><th className="pr-6 text-right">Derived from API</th><th className="text-right">Difference</th></tr></thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.steamId}>
              <td className="pr-6">{d.name}</td>
              <td className="pr-6 text-right tabular-nums">{usd(d.reportUsd)}</td>
              <td className="pr-6 text-right tabular-nums">{usd(d.derivedUsd)}</td>
              <td className="text-right tabular-nums">{usd(d.diffUsd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MonthDetails({ m, isAdmin, onChanged }: { m: Month; isAdmin: boolean; onChanged: () => Promise<unknown> }) {
  const [reason, setReason] = useState('');
  const url = `/api/steam/reconciliation/${m.year}/${m.month}/override`;
  const save = useMutation({ mutationFn: () => api('PUT', url, { reason }), onSuccess: async () => { setReason(''); await onChanged(); } });
  const remove = useMutation({ mutationFn: () => api('DELETE', url), onSuccess: onChanged });
  const error = save.error ?? remove.error;
  return (
    <div className="space-y-3 bg-slate-50 px-4 py-3 dark:bg-slate-900">
      {m.status === 'report_missing' && <p className="text-sm">No Steam report for {period(m)} is imported. Upload it, or document why the month settles without one.</p>}
      <DiffTable title="Differences above the 0.01 USD tolerance" rows={m.diffs} />
      <DiffTable title="Rounding differences within the tolerance" rows={m.roundingDiffs} />
      {m.reportOnly.length > 0 && (
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Report-only lines (not delivered by the Financials API): {m.reportOnly.map((r) => `${r.name} ${usd(r.revenueShareUsd)}`).join(', ')}
        </p>
      )}
      {m.reportId && <a className="text-sm underline" href={`/api/steam/reports/${m.reportId}/document`}>Download the Steam report</a>}
      {m.override && (
        <div className="text-sm">
          <p>
            <span className="font-medium">Override</span> ({new Date(m.override.createdAt).toLocaleString('en-GB')}){m.override.current ? '' : ' - no longer valid: the differences changed since'}: {m.override.reason}
          </p>
          {isAdmin && <Button className="mt-2" onClick={() => remove.mutate()} disabled={remove.isPending}>Remove override</Button>}
        </div>
      )}
      {isAdmin && (m.status === 'mismatch' || m.status === 'report_missing') && (
        <form className="max-w-2xl space-y-2" onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">Accept these differences: reason (kept in the audit log)</span>
            <textarea className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <Button type="submit" disabled={save.isPending || reason.trim().length < 10}>Override</Button>
        </form>
      )}
      {error && <ErrorText>{error instanceof ApiError ? error.message : 'The change failed'}</ErrorText>}
    </div>
  );
}

export function ReportsPage() {
  const isAdmin = useMe().user?.role === 'admin';
  const client = useQueryClient();
  const months = useQuery({ queryKey: ['steam-reconciliation'], queryFn: () => api<Month[]>('GET', '/api/steam/reconciliation') });
  const [open, setOpen] = useState<string | null>(null);
  const refresh = () => client.invalidateQueries({ queryKey: ['steam-reconciliation'] });
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const payload = { files: await Promise.all(files.map(async (f) => ({ filename: f.name, html: await f.text() }))) };
      return (await api<{ results: UploadResult[] }>('POST', '/api/steam/reports', payload)).results;
    },
    onSettled: refresh,
  });
  const onFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    if (files.length > 0) upload.mutate(files);
  };
  const blocked = months.data?.filter((m) => m.status === 'mismatch' || m.status === 'report_missing').length ?? 0;

  return (
    <section className="space-y-6">
      <h1 className="text-xl font-semibold">Monthly Steam reports</h1>
      <p className="max-w-3xl text-sm text-slate-500">
        Each month's Steam report is checked against the revenue share derived from the Financials API (net sales x Valve rate, cut to cents). A mismatch above 0.01 USD per product or a missing report blocks settlement of that month until it matches or carries an override with a reason.
      </p>
      {isAdmin && (
        <label className="block text-sm">
          <span className="mb-1 block text-slate-600 dark:text-slate-400">Upload Steam Sales Report HTML files (one or several)</span>
          <input type="file" accept=".html,.htm,text/html" multiple onChange={onFiles} disabled={upload.isPending} />
        </label>
      )}
      {upload.isError && <ErrorText>{upload.error instanceof ApiError ? upload.error.message : 'The upload failed'}</ErrorText>}
      {upload.data && (
        <ul className="text-sm">
          {upload.data.map((r) => (
            <li key={r.filename} className={r.ok ? '' : 'text-red-700 dark:text-red-400'}>
              {r.filename}: {r.ok ? `${period(r)} ${r.replaced ? 'replaced' : 'imported'}, ${r.lines} products` : r.message}
            </li>
          ))}
        </ul>
      )}
      {blocked > 0 && <p className="text-sm font-medium text-red-700 dark:text-red-400">{blocked} month(s) block settlement.</p>}
      <table className="text-sm">
        <thead className="text-slate-500">
          <tr><th className="pr-8 text-left">Month</th><th className="pr-8 text-left">Status</th><th className="pr-8 text-right">Report revenue share</th><th className="pr-8 text-right">Derived from API</th><th className="text-right">Differences</th></tr>
        </thead>
        <tbody>
          {months.data?.map((m) => {
            const key = period(m);
            return (
              <Fragment key={key}>
                <tr className="cursor-pointer border-t border-slate-200 hover:bg-slate-100 dark:border-slate-800 dark:hover:bg-slate-800" onClick={() => setOpen(open === key ? null : key)}>
                  <td className="pr-8">{key}</td>
                  <td className={`pr-8 ${STATUS[m.status].className}`}>{STATUS[m.status].label}</td>
                  <td className="pr-8 text-right tabular-nums">{usd(m.reportRevenueShareUsd)}</td>
                  <td className="pr-8 text-right tabular-nums">{usd(m.derivedRevenueShareUsd)}</td>
                  <td className="text-right tabular-nums">{m.diffs.length || '-'}</td>
                </tr>
                {open === key && (
                  <tr><td colSpan={5}><MonthDetails m={m} isAdmin={isAdmin} onChanged={refresh} /></td></tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {months.data?.length === 0 && <p className="text-sm text-slate-500">No Steam report is imported yet.</p>}
    </section>
  );
}
